import type { CSSProperties, ElementType, FC, ReactNode } from 'react'

import { Fragment, useRef } from 'react'

import { cn } from '@/lib/utils'
import { gsap, useGsap, prefersReducedMotion } from '@/lib/gsap'
import { splitToWords } from '@/lib/split'

interface SplitTextProps {
    text: string
    as?: ElementType
    className?: string
    style?: CSSProperties
    /* 'chars' reproduces the reference exactly: one span per glyph. 'words' is
       used for long body copy where a per-glyph stagger would read as noise. */
    mode?: 'chars' | 'words'
    /* Masked reveals clip each word box so glyphs rise out of nothing. */
    mask?: boolean
    stagger?: number
    delay?: number
    duration?: number
    rotate?: number
    /* 'scroll' builds its own ScrollTrigger, 'mount' fires immediately,
       'none' leaves the pieces parked for a parent timeline to pick up. */
    trigger?: 'scroll' | 'mount' | 'none'
    start?: string
    id?: string
}

export const SplitText: FC<SplitTextProps> = ({
    text,
    as: Tag = 'span',
    className,
    style,
    mode = 'chars',
    mask = true,
    stagger = 0.014,
    delay = 0,
    duration = 1.05,
    rotate = 0,
    trigger = 'scroll',
    start = 'top 85%',
    id
}): ReactNode => {
    const scope = useRef<HTMLElement>(null)
    const pieces = splitToWords(text)

    useGsap(() => {
        if (trigger === 'none') {
            return
        }

        const root = scope.current

        if (!root) {
            return
        }

        const targets = root.querySelectorAll<HTMLElement>('[data-split-piece]')

        if (targets.length === 0) {
            return
        }

        if (prefersReducedMotion()) {
            gsap.set(targets, { yPercent: 0, opacity: 1, rotate: 0 })

            return
        }

        const tween = {
            yPercent: 0,
            opacity: 1,
            rotate: 0,
            duration,
            delay,
            stagger,
            ease: 'noma'
        }

        if (trigger === 'mount') {
            gsap.to(targets, tween)

            return
        }

        gsap.to(targets, {
            ...tween,
            scrollTrigger: {
                trigger: root,
                start,
                once: true
            }
        })
    }, scope, [text, trigger, mode])

    /* The mask has to be padded on BOTH sides. These headings run at a
       line-height below 1, so the glyphs overflow their own line box and a
       bare `overflow: hidden` shears the caps off the top. The padding
       restores the clip area and the negative margin gives the layout height
       back, so line spacing is unchanged. */
    const maskStyle: CSSProperties = {
        overflow: 'hidden',
        paddingTop: '0.26em',
        paddingBottom: '0.26em',
        marginTop: '-0.26em',
        marginBottom: '-0.26em'
    }

    /* Parked far enough down to clear the padded box, not just the line box. */
    const pieceStyle: CSSProperties = {
        transform: `translateY(${mask ? 140 : 42}%) rotate(${rotate}deg)`,
        opacity: mask ? 1 : 0
    }

    return (
        <Tag
            id={id}
            ref={scope}
            className={cn('block', className)}
            style={style}
            aria-label={text}
        >
            <span aria-hidden='true'>
                {pieces.map((piece, pieceIndex) => (
                    <Fragment key={`${piece.word}-${pieceIndex}`}>
                        <span
                            className='split-word'
                            style={mask ? maskStyle : undefined}
                        >
                            {mode === 'chars'
                                ? piece.chars.map((char, charIndex) => (
                                    <span
                                        key={`${char}-${charIndex}`}
                                        data-split-piece='char'
                                        className='split-char'
                                        style={pieceStyle}
                                    >
                                        {char}
                                    </span>
                                ))
                                : (
                                    <span
                                        data-split-piece='word'
                                        className='split-char'
                                        style={pieceStyle}
                                    >
                                        {piece.word}
                                    </span>
                                )}
                        </span>

                        {/* The gap has to be a text node in the PARENT line box.
                            Tucked inside the word span it is invisible to the
                            line breaker, so the text can neither wrap between
                            words nor justify. */}
                        {piece.trailingSpace ? ' ' : null}
                    </Fragment>
                ))}
            </span>
        </Tag>
    )
}
